import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { ButtonNew as Button } from "components/buttonNew";
import "./ticketRedemption.scss";

interface IProps {
  id: string;
  value: number;
  max: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}

export default function TicketRedemptionQuantityInput(props: IProps) {
  const { id, value, max, disabled = false, onChange } = props;
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      // Only scroll if the stepper is clipped (e.g. last row under the footer)
      containerRef.current?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, []);

  const clamp = (nextValue: number) => Math.min(Math.max(1, nextValue), Math.max(1, max));

  return (
    <div
      ref={containerRef}
      className="ticket-redemption-quantity"
      onClick={event => event.stopPropagation()}
    >
      <Button
        type="secondary"
        size="small"
        className="ticket-redemption-quantity__button"
        aria-label={t("Decrease redemption quantity")}
        disabled={disabled || value <= 1}
        onClick={() => onChange(clamp(value - 1))}
      >
        <FontAwesomeIcon icon={faMinus} />
      </Button>
      <input
        id={id}
        className="ticket-redemption-quantity__input"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={String(value)}
        disabled={disabled}
        aria-label={t("Redemption quantity")}
        onChange={event => {
          const nextValue = Number(event.target.value.replace(/\D/g, ""));
          if (!Number.isFinite(nextValue) || event.target.value === "") {
            return;
          }
          onChange(clamp(nextValue));
        }}
      />
      <Button
        type="secondary"
        size="small"
        className="ticket-redemption-quantity__button"
        aria-label={t("Increase redemption quantity")}
        disabled={disabled || value >= max}
        onClick={() => onChange(clamp(value + 1))}
      >
        <FontAwesomeIcon icon={faPlus} />
      </Button>
    </div>
  );
}
